import React, { FC } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MuiButton from "@mui/material/Button";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import Select from "@/components/Select";
import { WEEK_DAYS } from "@/domain/externalProviders.interface";
import { ExternalProviderFormData } from "./validation.schema";

type Props = {
  langIndex: number;
  onRemove: () => void;
  canRemove: boolean;
  languageOptions: { key: string; value: string }[];
};

const dayOptions = WEEK_DAYS.map((day) => ({ key: day, value: day }));

const ProviderLanguageFields: FC<Props> = ({
  langIndex,
  onRemove,
  canRemove,
  languageOptions,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<ExternalProviderFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `languages.${langIndex}.hours` as const,
  });

  const langErrors = errors.languages?.[langIndex];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="bodyMedium" sx={{ fontWeight: 600 }}>
          Language #{langIndex + 1}
        </Typography>
        <IconButton
          size="small"
          disabled={!canRemove}
          onClick={onRemove}
          aria-label="remove language"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={"16px"}>
        <Grid item xs={12} sm={4}>
          <Controller
            name={`languages.${langIndex}.language` as const}
            control={control}
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={languageOptions}
                value={field.value as string}
                label="Language"
                id={`provider-lang-${langIndex}`}
                required
                error={!!langErrors?.language}
                errorMessage={langErrors?.language?.message || ""}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Controller
            name={`languages.${langIndex}.description` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={`provider-lang-desc-${langIndex}`}
                label="Description"
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`languages.${langIndex}.services` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={`provider-lang-services-${langIndex}`}
                label="Services (comma separated)"
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`languages.${langIndex}.restrictions` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={`provider-lang-restrictions-${langIndex}`}
                label="Restrictions (comma separated)"
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="bodyMedium" sx={{ fontWeight: 500 }}>
              Hours
            </Typography>
            <MuiButton
              size="small"
              variant="text"
              startIcon={<AddIcon />}
              onClick={() => append({ day: "Monday", hour: "" })}
            >
              Add hours
            </MuiButton>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {fields.map((hourField, hourIndex) => {
              const hourErrors = langErrors?.hours?.[hourIndex];
              return (
                <Grid container spacing={"12px"} key={hourField.id}>
                  <Grid item xs={5} sm={4}>
                    <Controller
                      name={
                        `languages.${langIndex}.hours.${hourIndex}.day` as const
                      }
                      control={control}
                      render={({ field }) => (
                        <Select
                          handleChange={(value) => field.onChange(value)}
                          data={dayOptions}
                          value={field.value as string}
                          label="Day"
                          id={`provider-hour-day-${langIndex}-${hourIndex}`}
                          required
                          error={!!hourErrors?.day}
                          errorMessage={hourErrors?.day?.message || ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={7}>
                    <Controller
                      name={
                        `languages.${langIndex}.hours.${hourIndex}.hour` as const
                      }
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id={`provider-hour-value-${langIndex}-${hourIndex}`}
                          label="Hour (e.g. 09:00-17:00)"
                          variant="outlined"
                          required
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          error={!!hourErrors?.hour}
                          helperText={hourErrors?.hour?.message || null}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    sm={1}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => remove(hourIndex)}
                      aria-label="remove hours"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ProviderLanguageFields;
